<script lang="ts">
    import { onDestroy } from 'svelte'

    import Info from './Info.svelte'
    import Toggle from './Toggle.svelte'
    import { cfg, consent, consentedCats, documents, lg, status } from './consent'
    import type { Category, Technology } from './types'
    import { chevronDownIcon, chevronUpIcon, closeIcon, infoIcon } from './icons'
    import Icon from './Icon.svelte'
    import { SvelteSet } from 'svelte/reactivity'

    interface Props {
        mode: 'banner' | 'modal'
        close: () => void
        saved: () => void
        toModal: () => void
    }

    let { mode, close, saved, toModal }: Props = $props()

    interface LCat {
        cat: Category
        state: boolean
    }
    let cats: LCat[] = $state([])
    // let openCats: Set<string> = $state(new Set())
    let openCats: Set<string> = new SvelteSet()
    let loaded = $state(false)
    let allAreRequired: boolean = $state(false)

    function loadConsent(saved: Set<string>, loadDefaults: boolean = false) {
        loaded = true
        let allReq = true
        let lcats: LCat[] = []
        for (const cat of $cfg.cats) {
            lcats.push({
                cat: cat,
                state: cat.mandatory ? true : loadDefaults ? cat.default : saved.has(cat.id),
            })
            if (!cat.mandatory) allReq = false
        }
        allAreRequired = allReq
        cats = lcats
    }
    $effect(() => {
        if ((!loaded && $status == 'new') || $status == 'ok') loadConsent($consentedCats, $status == 'new')
    })

    function toggle(lcat: LCat) {
        if (lcat.cat.mandatory) return
        lcat.state = !lcat.state
        // Force trigger
        cats = cats
    }

    function setAll() {
        for (const lcat of cats) {
            lcat.state = true
        }
        cats = cats
    }
    function setNone() {
        for (const lcat of cats) {
            lcat.state = lcat.cat.mandatory
        }
        cats = cats
    }

    function save() {
        const lconsent: { [key: string]: boolean } = {}
        for (const lcat of cats) {
            lconsent[lcat.cat.id] = lcat.state
        }
        consent.setConsent(lconsent)
        saved()
    }

    function toggleOpen(lcat: LCat) {
        if (openCats.has(lcat.cat.id)) {
            openCats.delete(lcat.cat.id)
        } else {
            openCats.add(lcat.cat.id)
        }
    }

    let infoItem: Technology | null = $state(null)

    onDestroy(() => {
        if (document.body.classList.contains('conb-grayed-out')) document.body.classList.remove('conb-grayed-out')
    })
    if (mode == 'modal') {
        document.body.classList.add('conb-grayed-out')
    }

    function iabout(item: Technology) {
        return $cfg['info-about'][$lg.id].replace('{}', item.name[$lg.id])
    }
</script>

<div class="conb-title">
    <span class="conb-text" id="conb-desc" role="heading" aria-level="1">{$cfg.title[$lg.id]}</span>
    <span class="lang-list">
        {#each $cfg.languages as lang}
            <button
                class="conb-lang"
                type="button"
                onclick={() => {
                    consent.setLg(lang.id)
                }}
            >
                {lang.text}
            </button>
        {/each}
    </span>
    {#if mode == 'modal'}
        <div class="conb-close">
            <button
                class="conb-iconb"
                type="button"
                aria-label={$cfg['close-text'][$lg.id]}
                onclick={() => {
                    close()
                }}><Icon icon={closeIcon} /></button
            >
        </div>
    {/if}
</div>

<div class="conb-intro">
    {@html $cfg[`${mode}-intro-html`][$lg.id]}
</div>

{#if mode == 'banner'}
    <div class="conb-simple-cats">
        {#if cats !== undefined}
            {#each cats as lcat}
                <div class="conb-cat">
                    <div class="conb-name" id="conbi-l-{lcat.cat.id}">{lcat.cat.title[$lg.id]}</div>
                    <div class="conb-simple-tgl">
                        <Toggle
                            state={lcat.state}
                            disabled={lcat.cat.mandatory}
                            ariaLabelledby="conbi-l-{lcat.cat.id}"
                            toggle={() => {
                                toggle(lcat)
                            }}
                        />
                    </div>
                </div>
            {/each}
        {/if}
    </div>
{/if}

<div class="conb-links">
    {#each $documents as doc}
        <a href={doc.link}>{doc.title}</a>
    {/each}
    {#if mode == 'banner'}
        <a
            onclick={(e) => {
                e.preventDefault()
                toModal()
            }}
            href="/">{$cfg['config-text'][$lg.id]}</a
        >
    {/if}
</div>

{#if mode == 'modal'}
    <div class="conb-cats">
        {#if cats !== undefined}
            {#each cats as lcat}
                <div class="conb-cat" class:conb-open={openCats.has(lcat.cat.id)} class:conb-closed={!openCats.has(lcat.cat.id)}>
                    <div class="conb-name" id="conbi-l-{lcat.cat.id}">{lcat.cat.title[$lg.id]}</div>
                    <div class="conb-desc" id="conbi-d-{lcat.cat.id}">{@html lcat.cat.text[$lg.id]}</div>
                    <div class="conb-actions">
                        <Toggle
                            ariaLabelledby="conbi-l-{lcat.cat.id}"
                            ariaDescribedby="conbi-d-{lcat.cat.id}"
                            state={lcat.state}
                            disabled={lcat.cat.mandatory}
                            toggle={() => {
                                toggle(lcat)
                            }}
                        />
                        <button
                            type="button"
                            class="conb-opener"
                            role="switch"
                            aria-checked={openCats.has(lcat.cat.id)}
                            aria-label={$cfg['info-about'][$lg.id].replace('{}', lcat.cat.title[$lg.id])}
                            onclick={() => {
                                toggleOpen(lcat)
                            }}><Icon icon={openCats.has(lcat.cat.id) ? chevronUpIcon : chevronDownIcon} /></button
                        >
                    </div>
                    <div class="conb-items">
                        {#each lcat.cat.items as item}
                            <div class="conb-item">
                                <div class="conb-item-name">{item.name[$lg.id]}</div>
                                <div class="conb-item-info">
                                    <button
                                        type="button"
                                        class="conb-iconb"
                                        aria-label={iabout(item)}
                                        onclick={() => {
                                            infoItem = item
                                        }}><Icon icon={infoIcon} /></button
                                    >
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            {/each}
        {/if}
    </div>
    <div class="conb-sep"></div>
{/if}

<div class="conb-actions conb-n-actions-{allAreRequired ? 1 : 3}">
    <button type="button" class="conb-action conb-action-save" onclick={() => save()}>{$cfg['save-text'][$lg.id]}</button>
    {#if !allAreRequired}
        <button
            type="button"
            class="conb-action conb-action-reject"
            onclick={() => {
                setNone()
                save()
            }}>{$cfg['reject-text'][$lg.id]}</button
        >
        <button
            type="button"
            class="conb-action conb-action-all"
            onclick={() => {
                setAll()
                save()
            }}>{$cfg['all-text'][$lg.id]}</button
        >
    {/if}
</div>

{#if infoItem != null}
    <Info
        item={infoItem}
        close={() => {
            infoItem = null
        }}
    />
{/if}
